import Backbone from "backbone";
import sleep from "nx-components/utils/sleep";
import { getTransitionPromise } from "nexia_mobile/components/Routing/HistoryPageTransitionController";

export default class Collection extends Backbone.Collection {
  static Sort = {
    ASC: "ASC",
    DESC: "DESC",
  };

  set(models, options) {
    if (options && options.hasOwnProperty("asyncParseDelay")) {
      return this.setAsync(models, options);
    } else {
      return super.set(models, options);
    }
  }

  // Duplicated from Backbone with sorting stuff removed, edited to support async
  async setAsync(passedModels, passedOptions) {
    /* eslint-disable flowtype/no-unused-expressions */
    await Promise.resolve(); // we need this because if models.length = 0, we never await and the function executes synchronously
    /* eslint-enable flowtype/no-unused-expressions */

    await getTransitionPromise();

    let models = passedModels;
    let options = passedOptions;
    if (models == null) {
      return undefined;
    }

    options = _.extend({ add: true, remove: true, merge: true }, options);
    if (options.parse && !this._isModel(models)) {
      models = await this._parseModelsAsync(models, options);
    }

    let singular = !_.isArray(models);
    models = singular ? [models] : models.slice();

    let at = options.at;
    if (at != null) {
      at = Number(at);
    }
    if (at > this.length) {
      at = this.length;
    }
    if (at < 0) {
      at += this.length + 1;
    }

    let set = [];
    let toAdd = [];
    let toMerge = [];
    let toRemove = [];
    let modelMap = {};

    let add = options.add;
    let merge = options.merge;
    let remove = options.remove;

    // Turn bare objects into model references, and prevent invalid models
    // from being added.
    let model;
    let i;
    for (i = 0; i < models.length; i++) {
      await getTransitionPromise();

      model = models[i];

      // If a duplicate is found, prevent it from being added and
      // optionally merge it into the existing model.
      let existing = this.get(model);
      if (existing) {
        if (merge && model !== existing) {
          let attrs = this._isModel(model) ? model.attributes : model;
          if (options.parse) {
            attrs = await existing.parse(attrs, options);
          }
          existing.set(attrs, options);
          toMerge.push(existing);
        }
        if (!modelMap[existing.cid]) {
          modelMap[existing.cid] = true;
          set.push(existing);
        }
        models[i] = existing;

        // If this is a new, valid model, push it to the `toAdd` list.
      } else if (add) {
        models[i] = this._prepareModel(model, options);
        model = models[i];
        if (model) {
          toAdd.push(model);
          this._addReference(model, options);
          modelMap[model.cid] = true;
          set.push(model);
        }
      }
    }

    // Remove stale models.
    if (remove) {
      for (i = 0; i < this.length; i++) {
        model = this.models[i];
        if (!modelMap[model.cid]) {
          toRemove.push(model);
        }
      }
      if (toRemove.length) {
        this._removeModels(toRemove, options);
      }
    }

    let replace = add && remove;
    if (set.length && replace) {
      this.models = set;
      this.length = this.models.length;
    } else if (toAdd.length) {
      const index = at == null ? this.length : at;
      this.models.splice(index, 0, ...toAdd);
      this.length = this.models.length;
    }

    // Unless silenced, it's time to fire all appropriate add/sort/update events.
    if (!options.silent) {
      for (i = 0; i < toAdd.length; i++) {
        if (at != null) {
          options.index = at + i;
        }
        model = toAdd[i];
        model.trigger("add", model, this, options);
      }
      if (toAdd.length || toRemove.length || toMerge.length) {
        options.changes = {
          added: toAdd,
          removed: toRemove,
          merged: toMerge,
        };
        this.trigger("update", this, options);
      }
    }

    // Return the added (or merged) model (or models).
    return singular ? models[0] : models;
  }

  parse(obj) {
    if (!this.model || !(obj instanceof Array)) {
      return obj;
    }
    let ModelClass = this.model;
    return obj.map((item) => new ModelClass(item, { parse: true }));
  }

  async _parseModelsAsync(obj, options) {
    this._warnIfSubclassInvalid();
    if (!this.model || !(obj instanceof Array)) {
      return obj;
    }
    let ModelClass = this.model;
    let models = [];
    for (let item of obj) {
      await sleep(options.asyncParseDelay);
      let model = new ModelClass(item, { parse: true });
      models.push(model);
    }
    return models;
  }

  _warnIfSubclassInvalid() {
    let subclass = Object.getPrototypeOf(this);
    if (
      subclass.hasOwnProperty("parse") &&
      !subclass.hasOwnProperty("_parseModelsAsync")
    ) {
      console.error(
        `Subclass ${this.constructor.name} implements parse but not _parseModelsAsync`
      );
    }
    if (
      subclass.hasOwnProperty("_parseModelsAsync") &&
      !subclass.hasOwnProperty("parse")
    ) {
      console.error(
        `Subclass ${this.constructor.name} implements _parseModelsAsync but not parse`
      );
    }
  }

  setSortAttribute(attr, direction) {
    // TODO: what about server-side sorting for paged collections?
    // perhaps do something entirely different here if this collection came from
    // a media type that supports sorted server queries...
    this.comparator = (model) => model.get(attr);
    if (_.isString(direction)) {
      this._sortDirection = direction.toUpperCase();
    }
  }

  sortBy(iterator, context) {
    let obj = this.models;
    let direction = this._sortDirection || this.constructor.Sort.ASC;
    return _.pluck(
      _.map(obj, (value, index, list) => ({
        value,
        index,
        criteria: iterator.call(context, value, index, list),
      })).sort((left, right) => {
        // swap a and b for reverse sort
        let criteriaA = direction === "ASC" ? left.criteria : right.criteria;
        let criteriaB = direction === "ASC" ? right.criteria : left.criteria;
        if (criteriaA !== criteriaB) {
          if (criteriaA > criteriaB || criteriaA === undefined) {
            return 1;
          }
          if (criteriaA < criteriaB || criteriaB === undefined) {
            return -1;
          }
        }
        return left.index < right.index ? -1 : 1;
      }),
      "value"
    );
  }
}



// WEBPACK FOOTER //
// ./src/framework/collection.js